/**
 * Single source of truth for the user-facing plan labels + colors.
 *
 * Backend enum (`User.plan` / `Subscription.plan`) uses the raw keys
 * (free / pro / premium / enterprise / enterprise_pending), but the UI
 * has different naming ("Essentiel", "Institution", …). Every screen
 * that displays a plan must import from here — otherwise we drift and
 * the admin invitations panel ends up showing "Pro" while the rest of
 * the app shows "Essentiel".
 */
export type PlanKey = 'free' | 'pro' | 'premium' | 'enterprise' | 'enterprise_pending'

export interface PlanConfig {
  label: string
  colorScheme: string
}

export const PLAN_CONFIG: Record<PlanKey, PlanConfig> = {
  free: { label: 'Sans abonnement', colorScheme: 'gray' },
  pro: { label: 'Essentiel', colorScheme: 'teal' },
  premium: { label: 'Premium', colorScheme: 'orange' },
  enterprise: { label: 'Institution', colorScheme: 'purple' },
  enterprise_pending: { label: 'Institution (en attente)', colorScheme: 'yellow' },
}

/** Plans the admin is allowed to pre-assign in an invitation.
 *  `enterprise_pending` is a transient state, not a deliberate choice. */
export const INVITABLE_PLANS: PlanKey[] = ['free', 'pro', 'premium', 'enterprise']

export function planLabel(key: string | null | undefined): string {
  if (!key) return PLAN_CONFIG.free.label
  return PLAN_CONFIG[key as PlanKey]?.label ?? key
}

export function planColor(key: string | null | undefined): string {
  if (!key) return PLAN_CONFIG.free.colorScheme
  return PLAN_CONFIG[key as PlanKey]?.colorScheme ?? 'gray'
}
